/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

/**
 * Validators for block configurations
 * Each validator returns { valid: true } or { valid: false, error: 'message' }
 */

export const validators = {
  /**
   * Validate bookmarks array structure
   */
  bookmarks: (data) => {
    if (!Array.isArray(data)) {
      return { valid: false, error: "Bookmarks must be an array" };
    }

    for (let i = 0; i < data.length; i++) {
      const bookmark = data[i];

      // Check required fields
      if (!bookmark.type || !bookmark.name || !bookmark.id) {
        return {
          valid: false,
          error: `Bookmark at index ${i} is missing required fields (type, name, id)`,
        };
      }

      // Check type values
      if (!["file", "folder"].includes(bookmark.type)) {
        return {
          valid: false,
          error: `Bookmark at index ${i} has invalid type '${bookmark.type}'. Must be 'file' or 'folder'`,
        };
      }

      // Check that id is a string
      if (typeof bookmark.id !== "string") {
        return {
          valid: false,
          error: `Bookmark at index ${i}: id must be a string`,
        };
      }

      // Check that name is a string
      if (typeof bookmark.name !== "string") {
        return {
          valid: false,
          error: `Bookmark at index ${i}: name must be a string`,
        };
      }
    }

    return { valid: true };
  },

  /**
   * Validate external apps links array structure
   */
  externalApps: (data) => {
    if (!Array.isArray(data)) {
      return { valid: false, error: "Links must be an array" };
    }

    for (let i = 0; i < data.length; i++) {
      const link = data[i];

      // Check required fields
      if (!link.type || !link.link) {
        return {
          valid: false,
          error: `Link at index ${i} is missing required fields (type, link)`,
        };
      }

      // Check type is a string (allow any type for flexibility)
      if (typeof link.type !== "string" || link.type.trim() === "") {
        return {
          valid: false,
          error: `Link at index ${i}: type must be a non-empty string`,
        };
      }

      // Check that link is a string
      if (typeof link.link !== "string") {
        return {
          valid: false,
          error: `Link at index ${i}: link must be a string`,
        };
      }
    }

    return { valid: true };
  },

  /**
   * Validate chart data structure
   */
  chartData: (data) => {
    if (typeof data !== "object" || data === null) {
      return { valid: false, error: "Chart data must be an object" };
    }

    // Check labels
    if (!data.labels || !Array.isArray(data.labels)) {
      return { valid: false, error: "Chart data must have a labels array" };
    }

    // Check datasets
    if (!data.datasets || !Array.isArray(data.datasets)) {
      return { valid: false, error: "Chart data must have a datasets array" };
    }

    // Validate each dataset
    for (let i = 0; i < data.datasets.length; i++) {
      const dataset = data.datasets[i];

      if (typeof dataset !== "object" || dataset === null) {
        return {
          valid: false,
          error: `Dataset at index ${i} must be an object`,
        };
      }

      if (!dataset.data || !Array.isArray(dataset.data)) {
        return {
          valid: false,
          error: `Dataset at index ${i} is missing data array`,
        };
      }

      // Check that all data values are numbers
      for (let j = 0; j < dataset.data.length; j++) {
        if (typeof dataset.data[j] !== "number") {
          return {
            valid: false,
            error: `Dataset at index ${i}: data[${j}] must be a number (got ${typeof dataset.data[j]})`,
          };
        }
      }

      // Check that labels and data have matching lengths
      if (dataset.data.length !== data.labels.length) {
        return {
          valid: false,
          error: `Dataset at index ${i}: data length (${dataset.data.length}) must match labels length (${data.labels.length})`,
        };
      }
    }

    return { valid: true };
  },
};
